// Pure helper functions shared across the app.
// No DOM access here — everything is unit-testable.

/** A database row: column id -> string value, plus internal bookkeeping. */
export type Row = Record<string, string> & {
  _synthetic?: boolean | string;
  _saved_at?: string;
  /** PocketBase record id (only present for backend-backed rows). */
  _pb_id?: string;
  /** PocketBase owner (user id) of the record. */
  _owner?: string;
};

/** True when a value is present (not null/undefined/blank after trim). */
export function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

/** Parse a possibly comma-decimal string into a number, NaN if not finite. */
export function num(value: unknown): number {
  const raw = String(value ?? "").trim().replace(",", ".");
  if (raw === "") return NaN;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : NaN;
}

/** Trim a value to a normalized string. */
export function normalizeValue(value: unknown): string {
  return String(value ?? "").trim();
}

/** Canonical name for each coronary vessel synonym cluster.
 *  Physicians write the same artery under different Russian abbreviations
 *  (both "ПМЖА" and "ПМЖВ" and "ПНА" for the LAD, etc.). Storing the
 *  canonical form keeps aggregate analyses honest — otherwise the same
 *  vessel gets split across three rows in every by-vessel count. Keys are
 *  compared uppercased+trimmed; unknown values pass through as-is so that
 *  legitimate vessels not covered by this mapping (ПКА, ЛКА, СтЛКА, ...)
 *  are preserved rather than silently blanked. */
const VESSEL_ALIASES: Record<string, string> = {
  "ПМЖА": "ПМЖА", "ПМЖВ": "ПМЖА", "ПНА": "ПМЖА",
  "ДА": "ДВ", "ДВ": "ДВ",
  "ВТК": "ВТК", "АТК": "ВТК",
  "ОА": "ОА", "ОВ": "ОА",
  "ЗМЖВ": "ЗМЖВ", "ЗМЖА": "ЗМЖВ",
};

export function normalizeVessel(value: unknown): string {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  const key = trimmed.toUpperCase();
  return VESSEL_ALIASES[key] ?? trimmed;
}

/** Round to 3 decimals and return as string (used for computed form fields). */
export function round(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

/** Sum of the finite values among a, b; NaN when none are finite. */
export function sumIfAny(a: number, b: number): number {
  const values = [a, b].filter(Number.isFinite);
  return values.length ? values.reduce((sum, value) => sum + value, 0) : NaN;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function clampInt(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function clampNum(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function sigmoid(x: number): number {
  if (x < -35) return 0;
  if (x > 35) return 1;
  return 1 / (1 + Math.exp(-x));
}

/** Distinct, trimmed, non-empty values sorted numerically then by ru locale. */
export function uniqueValues(values: unknown[]): string[] {
  const out: string[] = [];
  values.forEach((value) => {
    const text = String(value ?? "").trim();
    if (text && !out.includes(text)) out.push(text);
  });
  return out.sort((a, b) => Number(a) - Number(b) || a.localeCompare(b, "ru"));
}

/** Format a number for display; "—" when not finite. */
export function fmt(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const abs = Math.abs(value);
  if (abs !== 0 && abs < 0.001) return value.toExponential(2);
  return String(Math.round(value * 1000) / 1000);
}

/** Format a p-value: "<0.001" for very small, "—" when not finite. */
export function fmtP(value: number): string {
  if (!Number.isFinite(value)) return "—";
  if (value < 0.001) return "<0.001";
  return fmt(value);
}

/** Format a synthetic numeric value as a string ("" when not finite). */
export function fmtSynthetic(value: number): string {
  if (!Number.isFinite(value)) return "";
  return String(Math.round(value * 1000) / 1000);
}

/** ISO date stamp (YYYY-MM-DD) for export filenames. */
export function dateStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Quote a CSV cell when it contains separators/quotes/newlines. */
export function csvCell(value: unknown): string {
  const text = String(value ?? "");
  return /[;"\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export function escapeAttr(value: unknown): string {
  return escapeHtml(value);
}
